/**
 * SightingPatternWidget - 7-column day-of-week sighting frequency bars.
 * Renders a compact bar chart from /api/entities/:id/sighting-patterns data.
 * DOW order: Sun=0, Mon=1, … Sat=6 (PostgreSQL EXTRACT(DOW ...) convention).
 * @atomic @idempotent
 */
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const BAR_MAX_H = 28 // px — max bar height

const SightingPatternWidget = memo(function SightingPatternWidget({ counts }) {
  const { t } = useTranslation()

  const days = t('entities.entity.patterns.days', { returnObjects: true }) ||
    ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const maxCount = useMemo(
    () => Math.max(...(counts || [0]), 1),
    [counts]
  )

  // Get today's DOW (0=Sun) to highlight
  const todayDOW = new Date().getDay()

  if (!counts || counts.every(c => c === 0)) return null

  return (
    <div className="mt-2">
      <p className="text-xs text-gray-400 mb-1.5">
        {t('entities.entity.patterns.title', 'When they appear')}
      </p>
      <div className="flex items-end gap-1 h-8">
        {counts.map((count, dow) => {
          const barH = count > 0 ? Math.max(3, Math.round((count / maxCount) * BAR_MAX_H)) : 2
          const isToday = dow === todayDOW
          const hasActivity = count > 0

          return (
            <div
              key={dow}
              className="flex flex-col items-center gap-0.5"
              style={{ flex: 1 }}
              title={`${days[dow]}: ${count}`}
            >
              <div
                style={{ height: `${barH}px` }}
                className={[
                  'w-full rounded-sm transition-all',
                  hasActivity
                    ? isToday
                      ? 'bg-primary'
                      : 'bg-primary/40'
                    : 'bg-gray-100',
                ].join(' ')}
              />
              <span
                className={[
                  'text-[9px] leading-none',
                  isToday ? 'text-primary font-semibold' : 'text-gray-400',
                ].join(' ')}
              >
                {days[dow] ? days[dow][0] : ''}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
})

export default SightingPatternWidget
